/**
 * Drop-a-pin on a phone, aimed with a crosshair: where the crosshair sits on
 * the map you can see, and where a stop dropped there would go in the trip.
 * Pure, so it is unit-tested; HomeClient draws it.
 *
 *   ┌ map ────────────────────────────┐
 *   │ chips                           │  ← PIN_AIM_TOP
 *   │             ⊕  ← the crosshair  │  centred in the free strip
 *   │ Google logo strip               │  ← PIN_AIM_BANNER above the sheet
 *   ├──── sheet: "Tap the map…" ──────┤  (the prompt is docked in the sheet)
 */
import { placeNewStop } from '../../utils/tripPlanner';

/** Chip row + a little air: the top of the strip the crosshair centres in. */
export const PIN_AIM_TOP = 56;
/**
 * What sits between the free map and the sheet while armed: only Google's
 * logo strip (the instruction is docked inside the sheet's header).
 */
export const PIN_AIM_BANNER = 36;

/** The crosshair's point inside a W×H map with `inset` px covered by the sheet. */
export function pinAimPoint(width: number, height: number, inset: number): { x: number; y: number } {
  const free = Math.max(0, height - inset - PIN_AIM_BANNER - PIN_AIM_TOP);
  return { x: Math.round(width / 2), y: Math.round(PIN_AIM_TOP + free / 2) };
}

interface Pt {
  lat: number;
  lng: number;
}
interface NamedPt {
  lat?: number;
  lng?: number;
  name: string;
}

export interface PinGhost {
  /** Index the stop would be inserted at. */
  index: number;
  /** The stop before it (the dashed ghost starts here). */
  prev: NamedPt | null;
  /** The stop after it (null when it becomes the new destination). */
  next: NamedPt | null;
  /** "Goes between Lakes Entrance and Sydney" / "Becomes the new end, after Sydney". */
  label: string;
}

/** Where a pin at `p` lands in the trip: the same rule a real drop uses. */
export function pinGhost(stops: NamedPt[], p: Pt): PinGhost | null {
  if (stops.length < 1) return null;
  const { index, asNewEnd } = placeNewStop(stops, p);
  const prev = stops[index - 1] ?? null;
  const next = asNewEnd ? null : stops[index] ?? null;
  const label = next && prev
    ? `Goes between ${prev.name} and ${next.name}`
    : prev
      ? `Becomes the new end, after ${prev.name}`
      : next
        ? `Becomes the new start, before ${next.name}`
        : '';
  return { index, prev, next, label };
}
